import React from "react";
import { find, map } from "lodash";
import { getMetricLabel } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import Dropdown from "@radarly/components/panels/card-edition/sidebar/dropdown";
import { useCurrentProjectFocusesId } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { useCurrentWorkspaceId } from "@radarly/core/workspace/use-cases";
import { tr } from "@radarly/i18n";
import { Card } from "@radarly/models/cards";
import {
  setCardPrimaryMetric,
  setCardSecondaryMetric,
  setCardTertiaryMetric,
} from "@radarly/models/cards/editor";
import {
  getPrimaryMetricFromCard,
  getSecondaryMetricFromCard,
  getTertiaryMetricFromCard,
} from "@radarly/models/cards/metric";
import {
  Dataviz,
  isBubbleChartDataviz,
  isDoubleTimeChartDataviz,
  isScatterPlotDataviz,
} from "@radarly/models/dataviz";

export interface SharedPanelsCardEditionSidebarMetricFieldsProps {
  card: Card;
  setCard: (card: Card) => void;
}

enum Field {
  barchart = "barchart",
  linechart = "linechart",
  x_metric = "x_metric",
  y_metric = "y_metric",
  bubble_x = "bubble_x",
  bubble_y = "bubble_y",
  bubble_size = "bubble_size",
}

function getMetricFieldsFromDataviz(dataviz: Dataviz) {
  if (isDoubleTimeChartDataviz(dataviz)) {
    return [Field.barchart, Field.linechart];
  }
  if (isBubbleChartDataviz(dataviz)) {
    return [Field.bubble_size, Field.bubble_x, Field.bubble_y];
  }
  if (isScatterPlotDataviz(dataviz)) {
    return [Field.x_metric, Field.y_metric];
  }
  return null;
}

function getMetricsOptionsFromField(field: Field, locale: Locale) {
  const generateOption = (metric: Metric) => ({
    value: metric,
    label: getMetricLabel(locale, metric),
  });
  switch (field) {
    case Field.barchart:
    case Field.bubble_size:
      return [generateOption(Metric.Doc), generateOption(Metric.Repost)];
    case Field.bubble_x:
      return [
        generateOption(Metric.Reach),
        generateOption(Metric.NetSentiment),
      ];
    case Field.bubble_y:
      return [
        generateOption(Metric.Engagement),
        generateOption(Metric.EngagementPerPost),
      ];
    case Field.y_metric:
    case Field.x_metric:
    case Field.linechart:
      return [generateOption(Metric.Reach), generateOption(Metric.Engagement)];
  }
}

function getMetricFromCard(index: number, card: Card) {
  if (index === 0) return getPrimaryMetricFromCard(card);
  if (index === 1) return getSecondaryMetricFromCard(card);
  return getTertiaryMetricFromCard(card);
}

const Option = ({
  locale,
  field,
  metric,
  setMetric,
}: {
  locale: Locale;
  field: Field;
  metric: Metric;
  setMetric: (metric: Metric) => void;
}) => {
  const options = getMetricsOptionsFromField(field, locale);
  const currentOption = find(options, { value: metric });
  return (
    <Dropdown
      label={tr(locale, `CUSTOM_PAGES::EDITOR::DATAVIZ::FIELD::${field}`)}
      value={currentOption}
      options={options}
      onChange={({ value }) => setMetric(value as Metric)}
    />
  );
};

export default function SharedPanelsCardEditionSidebarMetricFields({
  card,
  setCard,
}: SharedPanelsCardEditionSidebarMetricFieldsProps) {
  const locale = useCurrentUserLocale();
  const availableFields = getMetricFieldsFromDataviz(card.dataviz);
  const workspaceId = useCurrentWorkspaceId();
  const projectFocuses = useCurrentProjectFocusesId();

  const updateCardMetric = (index: number, metric: Metric) => {
    if (index === 0) {
      return setCard(
        setCardPrimaryMetric(workspaceId, projectFocuses, card, metric)
      );
    }
    if (index === 1) {
      return setCard(setCardSecondaryMetric(card, metric));
    }
    return setCard(setCardTertiaryMetric(card, metric));
  };

  return (
    <>
      {map(availableFields, (field, i) => (
        <Option
          locale={locale}
          key={i}
          field={field}
          metric={getMetricFromCard(i, card)}
          setMetric={(metric) => updateCardMetric(i, metric)}
        />
      ))}
    </>
  );
}
